import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ShieldCheck, User, Trash2 } from "lucide-react";

const ROLE_LABEL = { admin: "Admin", sales_rep: "Sales Rep" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const load = () =>
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    const prev = users;
    setUsers((u) => u.map((x) => (x.id === id ? { ...x, role } : x)));
    try {
      await api.patch(`/users/${id}/role`, { role });
      toast.success(`Role updated to ${ROLE_LABEL[role]}`);
    } catch (e) {
      setUsers(prev);
      toast.error(apiError(e));
    }
  };

  const deleteUser = async (u) => {
    setDeletingId(u.id);
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((list) => list.filter((x) => x.id !== u.id));
      toast.success(`${u.name || u.email} removed`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeletingId(null);
    }
  };

  const adminCount = users.filter((u) => u.role === "admin").length;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div>
        <h1 className="font-heading text-2xl font-black tracking-tight">User Management</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          New @phsinc.com sign-ins are provisioned as <span className="font-semibold text-foreground">Sales Rep</span>.
          Promote teammates to Admin to grant access to integrations, pricing, products, and rate validation.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="label-caps">Total Users</div>
          <div className="metric-num mt-1 text-2xl font-black" data-testid="users-total">{users.length}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="label-caps">Admins</div>
          <div className="metric-num mt-1 text-2xl font-black text-[hsl(var(--primary))]" data-testid="users-admin-count">{adminCount}</div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Sign-in</TableHead>
              <TableHead className="w-44">Role</TableHead>
              <TableHead className="w-20 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No users yet.</TableCell></TableRow>
            ) : (
              users.map((u) => {
                const isSelf = me?.id === u.id;
                const lockLastAdmin = u.role === "admin" && adminCount <= 1;
                const disabled = isSelf || lockLastAdmin;
                return (
                  <TableRow key={u.id} className="border-border" data-testid={`user-row-${u.email}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-bold uppercase">
                          {(u.name || u.email || "U").slice(0, 2)}
                        </div>
                        <span className="text-sm font-semibold">{u.name || "—"}{isSelf && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell className="text-xs uppercase tracking-wider text-muted-foreground">{u.auth_provider === "google" ? "Google" : "Password"}</TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={(r) => changeRole(u.id, r)} disabled={disabled}>
                        <SelectTrigger
                          className={`h-8 w-40 border text-xs font-bold uppercase tracking-wider ${u.role === "admin" ? "text-[hsl(var(--primary))]" : ""}`}
                          data-testid={`user-role-${u.email}`}
                          title={isSelf ? "You can't change your own role" : lockLastAdmin ? "Can't demote the last admin" : ""}
                        >
                          <span className="flex items-center gap-1.5">
                            {u.role === "admin" ? <ShieldCheck className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                            <SelectValue />
                          </span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="sales_rep">Sales Rep</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      {disabled ? (
                        <Button
                          variant="ghost" size="icon" disabled
                          className="h-8 w-8 text-muted-foreground/40"
                          title={isSelf ? "You can't delete your own account" : "Can't delete the last admin"}
                          data-testid={`user-delete-${u.email}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost" size="icon"
                              disabled={deletingId === u.id}
                              className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--destructive))]"
                              title="Delete user"
                              data-testid={`user-delete-${u.email}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid="user-delete-dialog">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete user?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This permanently removes <span className="font-semibold text-foreground">{u.name || u.email}</span> ({u.email}) and signs them out. This can't be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid="user-delete-cancel">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteUser(u)}
                                className="bg-[hsl(var(--destructive))] text-white hover:bg-[hsl(var(--destructive))]/90"
                                data-testid="user-delete-confirm"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
